import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) { setUser(false); setLoading(false); return; }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      localStorage.removeItem('access_token');
      setUser(false);
    } finally { setLoading(false); }
  };

  const login = async (loginId, password) => {
    try {
      const { data } = await api.post('/auth/login', { login_id: loginId, password });
      if (data.access_token) localStorage.setItem('access_token', data.access_token);
      const { access_token, must_change_password, ...userData } = data;
      // Attach must_change_password onto user so the gate can pick it up
      setUser({ ...userData, must_change_password: !!must_change_password });
      return { success: true, must_change_password: !!must_change_password };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const changePassword = async (newPassword) => {
    try {
      await api.post('/auth/change-password', { new_password: newPassword });
      setUser((u) => (u ? { ...u, must_change_password: false } : u));
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem('access_token');
    setUser(false);
    // C17: redirect to /login so the user isn't left on a stale page
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  };

  const setupPassword = async (token, password) => {
    try {
      const { data } = await api.post('/auth/setup-password', { token, password });
      if (data.access_token) localStorage.setItem('access_token', data.access_token);
      const { access_token, ...userData } = data;
      setUser(userData);
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const updateLanguage = async (language) => {
    try {
      await api.put(`/users/${user.id}`, { language });
      setUser({ ...user, language });
    } catch { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setupPassword, updateLanguage, checkAuth, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
};
